<script setup>
import { computed, h } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';

import Table from 'dashboard/components/table/Table.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactCard from 'dashboard/components-next/Contacts/ContactsHeader/components/ContactCard.vue';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import { getContactChannelNames } from 'dashboard/components-next/Contacts/helpers/contactChannel';

const props = defineProps({
  contacts: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  pageIndex: {
    type: Number,
    default: 0,
  },
  totalCount: {
    type: Number,
    default: 0,
  },
  pageSize: {
    type: Number
  },
});

const emit = defineEmits(['pageChange', 'sendMessage', 'showContact']);

const { t } = useI18n();
const totalCount = computed(() => props.contacts.length);

const orderLabelOf = c => {
  if (!Array.isArray(c.orders) || !c.orders.length) return t('CONTACT_TABLE.NO_ORDERS');
  const first = c.orders[0];
  return first?.code || first?.name || '—';
};

const channelLabelsOf = contact => getContactChannelNames(contact);

// Cells
const ContactCell = cellProps => {
  const c = cellProps.row.original;
  return h(
    'div',
    { class: 'flex items-center gap-3 p-0' },
    [
      h(Thumbnail, {
        src: c.avatar_url || c.thumbnail,
        username: c.name,
        size: '32px',
        class: 'flex-shrink-0'
      }),
      h(
        'div',
        { class: 'text-n-slate-12 font-medium text-left truncate min-w-0' },
        c.name || '—'
      )
    ]
  );
};

const Badge = (text, cls = '') =>
  h(
    'span',
    { class: `px-2 py-0.5 rounded text-xs inline-flex items-center ${cls}` },
    text
  );

const ChannelPill = label =>
  h('span', {
    key: label,
    class:
      'px-2 py-0.5 rounded text-xs inline-flex items-center text-n-gray-7 bg-n-gray-1 dark:bg-[#23282F] dark:text-white',
  }, label);

const ChannelCell = cellProps => {
  const labels = channelLabelsOf(cellProps.row.original);
  if (!labels.length) {
    return h('div', { class: 'text-n-gray-5' }, '—');
  }

  return h(
    'div',
    { class: 'flex flex-wrap gap-2' },
    labels.map(ChannelPill)
  );
};

const OrderCell = cellProps => {
  const c = cellProps.row.original;
  return h('div', { class: 'text-n-gray-5' }, orderLabelOf(c));
};

const StatusCell = cellProps => {
  const value = (cellProps.getValue() || '').toLowerCase();
  const map = {
    online: { text: 'Online', cls: 'text-green-500 bg-[rgba(18,183,106,0.08)]' },
    offline: { text: 'Offline', cls: 'text-gray-500 bg-[rgba(37,43,55,0.2)]' },
  };
  const conf = map[value] || { text: value || '—', cls: 'text-gray-400 bg-transparent' };
  return Badge(conf.text, conf.cls);
};

const LabelCell = cellProps => {

  const labels = cellProps.getValue() || [];

  if (!Array.isArray(labels) || !labels.length) {
    return h('div', { class: 'text-n-gray-5' }, '—');
  }

  const maxDisplay = 2;
  const displayLabels = labels.slice(0, maxDisplay);
  const remainingCount = labels.length - maxDisplay;

  return h(
    'div',
    { class: 'flex gap-2' },
    [
      ...displayLabels.map(label => {
        const labelName = label.title || label.name || 'Unknown';
        const labelColor = label.color;

        return h(
          'div',
          { class: 'flex items-center gap-1 flex-shrink-0' },
          [
            h('div', {
              class: 'w-2 h-2 rounded-sm',
              style: { backgroundColor: labelColor }
            }),
            h('span', {
              class: 'text-n-gray-3 text-xs whitespace-nowrap'
            }, labelName)
          ]
        );
      }),
      ...(remainingCount > 0 ? [
        h('div', {
          class: 'flex items-center gap-1 flex-shrink-0'
        }, [
          h('div', {
            class: 'w-2 h-2 rounded-sm bg-gray-400'
          }),
          h('span', {
            class: 'text-n-gray-3 text-xs whitespace-nowrap'
          }, `+${remainingCount}`)
        ])
      ] : [])
    ]
  );
};

const ActionCell = cellProps =>
  h(
    'div',
    { 
      class: 'flex justify-end',
      onClick: (event) => event.stopPropagation()
    },
    h(
      Button,
      {
        size: 'sm',
        class: '!text-n-gray-7 dark:!text-white !bg-[#ECFDF3] dark:!bg-[#23282F] hover:!bg-grass-500',
        onClick: (event) => {
          event.stopPropagation();
          emit('sendMessage', cellProps.row.original?.id ?? cellProps.row.original);
        },
      },
      t('CONTACT_TABLE.SEND_MESSAGE')
    )
  );

// Columns
const columnHelper = createColumnHelper();
const columns = [
  columnHelper.accessor('name', { header: t('CONTACT_TABLE.CUSTOMER'), cell: ContactCell }),
  columnHelper.display({
    id: 'channel',
    header: t('CONTACT_TABLE.CHANNEL'),
    cell: ChannelCell,
  }),
  columnHelper.accessor('order', { header: t('CONTACT_TABLE.ORDER'), cell: OrderCell }),
  columnHelper.accessor('labels', { header: t('CONTACT_TABLE.LABELS'), cell: LabelCell }),
  columnHelper.display({
    id: 'actions',
    header: () => h('div', { class: 'w-full flex justify-end' }, t('CONTACT_TABLE.ACTIONS')),
    cell: ActionCell,
  }),
];

const paginationParams = computed(() => ({
  pageIndex: props.pageIndex,
  pageSize: props.pageSize,
}));

const table = useVueTable({
  get data() {
    return props.contacts;
  },
  columns,
  manualPagination: true,
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
  get rowCount() {
    return props.totalCount;
  },
  state: {
    get pagination() {
      return paginationParams.value;
    },
  },
  onPaginationChange: updater => {
    const next = updater(paginationParams.value);
    emit('pageChange', next.pageIndex);
  },
});
</script>

<template>
  <div class="flex flex-col flex-1 contact-table hidden lg:block pb-20">
    <Table :table="table" :fixed="true" :equal-columns="true" :row-clickable="true" @rowClick="(row) => emit('showContact', row.id)"
      />
    <div v-if="isLoading" class="flex items-center justify-center p-8">
      <span>{{ t('CONTACT_TABLE.LOADING') }}</span>
    </div>

    <div v-else-if="!isLoading && !contacts.length" class="flex items-center justify-center p-8">
      {{ t('CONTACT_TABLE.NO_CONTACTS') }}
    </div>
  </div>
  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:p-4 lg:hidden">
      <ContactCard
        v-for="contact in contacts"
        :key="contact.id"
        :contact="contact"
        @send-message="handleSendMessage"
        @show-contact="handleShowContact"
      />
  </div>
</template>
